import { Autocomplete } from '@yoast/ui-library';
import { useMemo, useState } from 'react';

import { commonLanguageOptionsFlat } from '../../utils/formOptions';

const LANGUAGE_OPTIONS = commonLanguageOptionsFlat;

type Props = {
  /** The currently-selected language name, or null. */
  value: string | null;
  /** Called with the selected language name, or null when cleared. */
  onChange: (name: string | null) => void;
};

/**
 * Searchable, single-select language picker for the brand-profile sidebar. The
 * stored value is the language name itself (unlike CountrySelect, which stores
 * an ISO code, because language is only ever used as prompt text). Restricting
 * to the list forces exactly one supported language.
 */
const LanguageSelect = ({ value, onChange }: Props) => {
  const [query, setQuery] = useState('');

  // Match an incoming value case-insensitively to a canonical option so a
  // differently-cased stored value still selects the right entry.
  const selected = useMemo(() => {
    const normalized = (value ?? '').trim().toLowerCase();
    if (!normalized) return null;
    return (
      LANGUAGE_OPTIONS.find(
        (option) => option.value.toLowerCase() === normalized,
      ) ?? null
    );
  }, [value]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return LANGUAGE_OPTIONS;
    return LANGUAGE_OPTIONS.filter((option) =>
      option.label.toLowerCase().includes(normalized),
    );
  }, [query]);

  return (
    <Autocomplete
      id="onboarding-language-select"
      label="Language"
      labelProps={{ className: 'yst-sr-only' }}
      value={selected?.value ?? null}
      nullable
      placeholder="Search languages…"
      onQueryChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setQuery(event.target.value)
      }
      onChange={(next: string | null) => {
        onChange(next ?? null);
        setQuery('');
      }}
      selectedLabel={selected?.label ?? ''}
    >
      {filtered.map((language) => (
        <Autocomplete.Option key={language.value} value={language.value}>
          {language.label}
        </Autocomplete.Option>
      ))}
    </Autocomplete>
  );
};

export default LanguageSelect;
